import {
  DefaultFetchError,
  FetchCreate,
  FetchDelete,
  FetchGet,
  FetchGetId,
  FetchSuccess,
  FetchUpdate,
  PagingDataResponse,
  PagingParams,
  useFetchCreate,
  useFetchDelete,
  useFetchGet,
  useFetchGetId,
  useFetchUpdate,
} from "../fetch";

// ============ TYPES ============

export interface ChartVaad {
  id: string;
  name: string;
}

export interface ChartMiddah {
  id: string;
  name: string;
}

export interface ChartExercise {
  id: string;
  name: string;
}

export interface ChartAvoda {
  id: string;
  repetitionsPerWeek: number;
  exercise: ChartExercise;
  createdAt: string;
  updatedAt: string;
}

export interface Chart {
  id: string;
  name: string;
  // Pure calendar dates ("YYYY-MM-DD"), timezone-independent — used for
  // display/editing. startTime/endTime are the same dates as UTC instants
  // (start-of-day / end-of-day UTC); reinterpreting them in the browser's
  // timezone shifts the day for non-UTC admins, so we read the date strings
  // instead and send startTime/endTime back.
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  defaultTasks: string[];
  vaad: ChartVaad;
  middah: ChartMiddah;
  avodas: ChartAvoda[];
  createdAt: string;
  updatedAt: string;
}

export interface ChartTableRow extends Chart {
  key: string;
}

export interface ChartTableData {
  data: ChartTableRow[];
  total: number;
}

// ============ PARAMS ============

export interface ChartCreateParams {
  name: string;
  startTime: string;
  endTime: string;
  vaad: {
    id: string;
  };
  middah: {
    id: string;
  };
  defaultTasks: string[];
  avodas: {
    exercise: {
      id: string;
    };
    repetitionsPerWeek: number;
  }[];
}

export interface ChartUpdateParams {
  name?: string;
  startTime?: string;
  endTime?: string;
  vaad?: {
    id: string;
  };
  middah?: {
    id: string;
  };
  defaultTasks?: string[];
  avodas?: {
    exercise: {
      id: string;
    };
    repetitionsPerWeek: number;
  }[];
}

export interface ChartGetParams
  extends
    PagingParams,
    Partial<Omit<Chart, "id" | "vaad" | "middah" | "avodas">> {
  orderByColumn?: keyof Omit<Chart, "id" | "vaad" | "middah" | "avodas">;
  vaadId?: string;
  middahId?: string;
}

// ============ HOOKS ============

export const useChartGet = <DD = PagingDataResponse<Chart>>(
  decorateData?: (data: PagingDataResponse<Chart>) => DD,
): FetchGet<PagingDataResponse<Chart>, ChartGetParams, DefaultFetchError, DD> =>
  useFetchGet("charts", {
    autoStart: false,
    startStateLoading: false,
    decorateData,
  });

export const useChartTableGet = () =>
  useChartGet<ChartTableData>(
    ({ data, meta }: PagingDataResponse<Chart>): ChartTableData => ({
      data: data.map((item) => ({
        key: item.id,
        ...item,
      })),
      total: meta.itemCount,
    }),
  );

export const useChartGetById = <DD = Chart>(
  id?: string,
  decorateData?: (data: Chart) => DD,
): FetchGetId<Chart, DefaultFetchError, unknown, DD> =>
  useFetchGetId("charts", id, {
    autoStart: !!id,
    decorateData,
  });

export const useChartCreate = (): FetchCreate<
  ChartCreateParams,
  DefaultFetchError,
  ChartCreateParams
> => useFetchCreate("charts");

export const useChartUpdate = (
  id?: string,
): FetchUpdate<ChartUpdateParams, DefaultFetchError, ChartUpdateParams> =>
  useFetchUpdate("charts", id);

export const useChartAttachmentsDelete = (
  id?: string,
): FetchDelete<FetchSuccess, DefaultFetchError> =>
  useFetchDelete("chart-attachments", id);
